import { Dispatch } from "redux";
import { fetchError, fetchStart, fetchSuccess } from "./Common";
import jwtAxios from "../../@crema/services/auth/jwt-auth";
import { AppActions } from "types";
import { AxiosResponse } from "axios";
import {
  GET_INVESTOR_DESK_SECTIONS,
  SET_INVESTOR_DESK_SECTION,
  GET_BOARD,
  GET_DIRECTORS,
  GET_REPORT_TYPES,
  GET_REPORT_YEARS,
  GET_FINANCIAL_DOCUMENTS,
  GET_REGULATION46_SECTIONS,
  GET_REGULATION46_DOCUMENTS,
} from "types/actions/investorDeskMaster.action";

// ─── Investor Desk content sections (investor_desk_reports) ───────────────

export const getInvestorDeskSections = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("investor-desk")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_INVESTOR_DESK_SECTIONS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getInvestorDeskSectionDetail = (id: string | number) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`investor-desk/${id}`)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: SET_INVESTOR_DESK_SECTION, payload: response.data.data });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const createInvestorDeskSection = (payload: any) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .post("investor-desk", payload)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const updateInvestorDeskSection = (id: string | number, payload: any) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put(`investor-desk/${id}`, payload)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const deleteInvestorDeskSection = (id: string | number) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .delete(`investor-desk/${id}`)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

// ─── Board of Directors ─────────────────────────────────────────────────

export const getBoard = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("board")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_BOARD, payload: response.data.data });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const updateBoard = (payload: any) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put("board", payload)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_BOARD, payload: response.data.data });
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const getDirectors = () => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .get("board/directors")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_DIRECTORS, payload: response.data.data || [] });
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const createDirector = (formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .post("board/directors", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const updateDirector = (id: number, formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put(`board/directors/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const deleteDirector = (id: number) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .delete(`board/directors/${id}`)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

// ─── Financial documents ─────────────────────────────────────────────────

export const getReportTypes = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("financial-reports/report-types")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_REPORT_TYPES, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getReportYears = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("financial-reports/report-years")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_REPORT_YEARS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getFinancialDocuments = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("financial-reports/documents")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_FINANCIAL_DOCUMENTS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getFinancialDocumentsByType = (typeId: string | number) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`financial-reports/type/${typeId}`)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_FINANCIAL_DOCUMENTS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getFinancialDocumentsByTypeAndYear = (typeId: string | number, year: string) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`financial-reports/type/${typeId}/year/${year}`)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_FINANCIAL_DOCUMENTS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const uploadFinancialDocument = (formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .post("financial-reports/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const updateFinancialDocument = (id: string | number, formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put(`financial-reports/documents/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const deleteFinancialDocument = (id: string | number) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .delete(`financial-reports/documents/${id}`)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

// ─── Regulation 46 ─────────────────────────────────────────────────────

export const getRegulation46Sections = () => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get("regulation-46/sections")
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_REGULATION46_SECTIONS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const updateRegulation46Section = (id: string | number, payload: any) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put(`regulation-46/sections/${id}`, payload)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const updateRegulation46Content = (id: string | number, content: string) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .put(`regulation-46/sections/${id}/content`, { content })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const getRegulation46Documents = (slug: string) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`regulation-46/sections/${slug}/documents`)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_REGULATION46_DOCUMENTS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const getRegulation46DocumentsByYear = (slug: string, year: string) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    jwtAxios
      .get(`regulation-46/sections/${slug}/documents/year/${year}`)
      .then((response) => {
        dispatch(fetchSuccess());
        dispatch({ type: GET_REGULATION46_DOCUMENTS, payload: response.data.data || [] });
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
      });
  };
};

export const uploadRegulation46Document = (slug: string, formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .post(`regulation-46/sections/${slug}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const deleteRegulation46Document = (slug: string, docId: string | number) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .delete(`regulation-46/sections/${slug}/documents/${docId}`)
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

export const getSubsidiariesList = () => {
  return (dispatch: Dispatch<AppActions>) => {
    return jwtAxios
      .get("subsidiaries")
      .then((response) => response.data?.data || [])
      .catch(() => []);
  };
};

export const getInvestorDeskDocuments = (investorDeskId: string | number) => {
  return (dispatch: Dispatch<AppActions>) => {
    dispatch(fetchStart());
    return jwtAxios
      .get(`financial-reports/investor-desk/reports/${investorDeskId}`)
      .then((response) => {
        dispatch(fetchSuccess());
        return response.data?.data || [];
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return [];
      });
  };
};

export const uploadInvestorDeskDocument = (investorDeskId: string | number, formData: FormData) => {
  return (dispatch: Dispatch<AppActions>): Promise<AxiosResponse<any>> => {
    dispatch(fetchStart());
    return jwtAxios
      .post(`financial-reports/investor-desk/reports/${investorDeskId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        dispatch(fetchSuccess());
        return response;
      })
      .catch((error) => {
        dispatch(fetchError(error?.response?.data?.message || error.message));
        return Promise.reject(error);
      });
  };
};

